import { useEffect, useState } from 'react';
import type { CustomField } from '@/common/api/types/customField';
import type { ItemCustomFieldValue } from '@/common/api/types/items';
import type { ItemTypeMap } from '@/common/api/types/itemType';
import { CustomFieldValueEditor } from '@/features/items/components/custom-field-value/CustomFieldValueEditor';
import type { TypedFieldValue } from '../../adapters/types';

interface CustomFieldValueWithAdapterProps {
  customField: CustomField;
  itemTypesById: ItemTypeMap;
  value?: ItemCustomFieldValue;
  isEditable: boolean;
  onUpdate: (fieldId: string, values: TypedFieldValue[]) => Promise<void>;
}

export const CustomFieldValueWithAdapter = ({
  customField,
  itemTypesById,
  value,
  isEditable = false,
  onUpdate,
}: CustomFieldValueWithAdapterProps) => {
  const { id: fieldId, type: fieldType } = customField;
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSuccess]);

  const handleChange = async (apiValue: unknown) => {
    try {
      setIsPending(true);
      setIsError(false);
      setError(null);
      setIsSuccess(false);

      await onUpdate(`${fieldId}`, [{ fieldType, value: apiValue } as TypedFieldValue]);

      setIsSuccess(true);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to update field'));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <CustomFieldValueEditor
      customField={customField}
      itemTypesById={itemTypesById}
      value={value}
      isEditable={isEditable}
      onUpdate={handleChange}
      isPending={isPending}
      isSuccess={isSuccess}
      isError={isError}
      error={error}
    />
  );
};
